import { Suspense } from 'react'
import { PanelResizeHandle } from 'react-resizable-panels'

import { useTheme } from '@chakra-ui/react'
import { AutoAnalytics } from '@verbit-ai/frontend-common'
import { LoadingDotsIcon } from '@verbit-ai/icons-library'
import { useMediaPlayerContext } from '@verbit-ai/verbit-connect-web/player-provider'

import { TranscriptUpdateEventQueueExecutor } from 'src/contexts/TranscriptUpdateEventQueueExecutor'
import { TranscriptViewerProvider } from 'src/contexts/TranscriptViewerProvider'
import { TranscriptHighlightProvider } from 'src/contexts/TranscriptHighLightContext'
import { useDimensions } from 'src/hooks/useDimensions'
import { usePanelDimensions } from 'src/hooks/usePanelDimensions'
import { Topbar } from 'src/components/Topbar/Topbar'
import { EmptyTranscriptScreen } from 'src/components/TranscriptViewer/EmptyTranscriptScreen'
import { isChromakeyModeActive, isPdfDisplayMode } from 'src/utils/env'
import { TranscriptToolbar } from 'src/components/TranscriptToolbar'
import { TranscriptErrorScreen } from 'src/components/TranscriptViewer/TranscriptErrorScreen'
import { useConfig } from 'src/contexts/ConfigurationContext'
import { AiPanelWrapper } from 'src/components/AiPanelWrapper/AiPanelWrapper'
import { TranscriptViewer } from 'src/components/TranscriptViewer/TranscriptViewer'
import { useVerbitConnectAvailability } from 'src/hooks/useVerbitConnectAvailability'
import { useTranscriptDimensions } from 'src/hooks/useTranscriptDimensions'
import { useIsTranscriptEmpty } from 'src/contexts/Transcript/TranscriptContext'
import { useTranscriptError } from 'src/contexts/Transcript/TranscriptErrorContext'
import { MediaPlayer } from 'src/components/VerbitWrappers/VerbitConnect'
import { DocumentViewer } from 'src/components/DocumentViewer'
import { analyticsService } from 'src/services/AnalyticsService'
import { InlineInconsistenciesProvider } from 'src/contexts/InlineInconsistenciesContext'

import { useStreamConcludedCheck, useRootAnalytics, useAiPanel, usePrerequisites, useConnectionListener } from './hooks'
import { EditingLockedBanner } from './EditingLockedBanner'
import { HelpChat } from './HelpChat'
import { MicSilenceWarning } from './MicSilenceWarning'
import { AIPanel } from './PanelGroup/AIPanel'
import { PanelGroup } from './PanelGroup'
import { TranscriptPanel } from './PanelGroup/Transcript'
import * as S from './styles'

const App = () => {
    const isTranscriptEmpty = useIsTranscriptEmpty()
    const { transcriptError } = useTranscriptError()

    const { dimensionsMeasurerRef } = useDimensions({ debounceMs: 1000 / 60 })
    const currentTheme = useTheme()
    const [{ isHeaderHidden }] = useConfig()
    const { isMediaRecorderAvailable, isMediaSourceAvailable, isMediaPlayerAvailable } = useVerbitConnectAvailability()
    const { isMediaPlayerDisplayed } = useMediaPlayerContext()

    const {
        isInitialFontAvailable,
        isReadyToDisplayTranscript,
        isReadyToDisplayViewerContainer,
        shouldDisplayGlobalOverlay,
        shouldDisplayTranscriptLoader,
        setIsInitialFontAvailable,
    } = usePrerequisites()

    const {
        panelRef: transcriptPanelRef,
        dimensions: transcriptPanelDimensions,
        handlePanelResize: handleTranscriptPanelResize,
    } = usePanelDimensions(isReadyToDisplayViewerContainer)

    const transcriptPanelWidth = transcriptPanelDimensions.width

    const {
        shouldDisplayAiPanel,
        isAIPanelOpened,
        isAnimatingPanelSize,
        onInsightsPanelAuthCheckCompleted,
        onPanelSizeModeChange,
        aiPanelRef,
        expandAIPanel,
        collapseAIPanel,
    } = useAiPanel()
    useRootAnalytics()
    useStreamConcludedCheck()
    useConnectionListener()

    const appBackgroundColor = currentTheme.palette.transcript.appBackgroundColors[currentTheme.appBackgroundColorIndex].colorValue

    const shouldDisplayMediaPlayer =
        transcriptPanelWidth > currentTheme.breakPoints.tabletMaxWidth &&
        (isMediaRecorderAvailable || isMediaSourceAvailable) &&
        isMediaPlayerAvailable &&
        isMediaPlayerDisplayed
    const shouldDisplayTopbar = !isChromakeyModeActive && !isHeaderHidden

    const { transcriptParagraphWidth } = useTranscriptDimensions(transcriptPanelWidth)
    const shouldDisplayError = !!transcriptError
    const shouldDisplayPanels = isReadyToDisplayViewerContainer && !shouldDisplayError

    return (
        <TranscriptViewerProvider isInitialFontAvailable={isInitialFontAvailable}>
            <TranscriptHighlightProvider>
                <TranscriptUpdateEventQueueExecutor>
                    <S.AppWrapper backgroundColor={appBackgroundColor}>
                        {analyticsService.areMandatoryFieldsSet && (
                            <AutoAnalytics projectName="Verbitext" onTrack={analyticsService.track.bind(analyticsService)} />
                        )}
                        {shouldDisplayTopbar && <Topbar />}
                        <HelpChat isAIPanelOpened={isAIPanelOpened} />

                        <S.AppContent $isHeaderHidden={!shouldDisplayTopbar} ref={dimensionsMeasurerRef}>
                            {shouldDisplayPanels && (
                                <InlineInconsistenciesProvider openAiPanel={expandAIPanel}>
                                    <PanelGroup>
                                        <TranscriptPanel
                                            ref={transcriptPanelRef}
                                            onResize={handleTranscriptPanelResize}
                                            containerWidth={transcriptPanelWidth}
                                            animateSize={isAnimatingPanelSize}>
                                            <S.TranscriptViewerLayoutContainer>
                                                {isMediaRecorderAvailable && <MicSilenceWarning />}
                                                <EditingLockedBanner />
                                                <S.TranscriptViewerContainer>
                                                    {isChromakeyModeActive ? <></> : <TranscriptToolbar />}
                                                    <S.TranscriptContentArea>
                                                        {shouldDisplayTranscriptLoader ? (
                                                            <S.TranscriptPanelLoader aria-busy="true">
                                                                <LoadingDotsIcon size={28} />
                                                            </S.TranscriptPanelLoader>
                                                        ) : (
                                                            isReadyToDisplayTranscript &&
                                                            (isTranscriptEmpty ? (
                                                                <EmptyTranscriptScreen />
                                                            ) : isPdfDisplayMode() ? (
                                                                <DocumentViewer />
                                                            ) : (
                                                                <TranscriptViewer
                                                                    onInitialFontAvailable={() => setIsInitialFontAvailable(true)}
                                                                />
                                                            ))
                                                        )}
                                                    </S.TranscriptContentArea>
                                                    {shouldDisplayMediaPlayer && (
                                                        <Suspense>
                                                            <S.MediaPlayerWrapperOuter>
                                                                <S.MediaPlayerWrapperInner
                                                                    $transcriptParagraphWidth={transcriptParagraphWidth}>
                                                                    <MediaPlayer />
                                                                </S.MediaPlayerWrapperInner>
                                                            </S.MediaPlayerWrapperOuter>
                                                        </Suspense>
                                                    )}
                                                </S.TranscriptViewerContainer>
                                            </S.TranscriptViewerLayoutContainer>
                                        </TranscriptPanel>
                                        {shouldDisplayAiPanel && (
                                            <>
                                                <PanelResizeHandle />
                                                <AIPanel
                                                    ref={aiPanelRef}
                                                    onExpand={expandAIPanel}
                                                    onCollapse={collapseAIPanel}
                                                    animateSize={isAnimatingPanelSize}>
                                                    <AiPanelWrapper
                                                        isOpen={isAIPanelOpened}
                                                        onAuthCheckCompleted={onInsightsPanelAuthCheckCompleted}
                                                        onPanelSizeModeChange={onPanelSizeModeChange}
                                                        onExpand={expandAIPanel}
                                                        onCollapse={collapseAIPanel}
                                                    />
                                                </AIPanel>
                                            </>
                                        )}
                                    </PanelGroup>
                                </InlineInconsistenciesProvider>
                            )}
                            {shouldDisplayError && <TranscriptErrorScreen error={transcriptError} />}
                            <S.LoaderContainer
                                $isVisible={shouldDisplayGlobalOverlay}
                                $backgroundColor={appBackgroundColor}
                                aria-hidden={!shouldDisplayGlobalOverlay}
                                aria-busy={shouldDisplayGlobalOverlay}>
                                <S.LoaderContent>
                                    <LoadingDotsIcon size={28} />
                                    <S.LoaderLabel>Loading transcript…</S.LoaderLabel>
                                </S.LoaderContent>
                            </S.LoaderContainer>
                        </S.AppContent>
                    </S.AppWrapper>
                </TranscriptUpdateEventQueueExecutor>
            </TranscriptHighlightProvider>
        </TranscriptViewerProvider>
    )
}

export default App
